import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, Mail, Building2, Search, RefreshCcw, ShieldCheck, ArrowRight, MessageSquare } from "lucide-react";
import { API } from "../lib/api";
import { useSEO } from "../hooks/useSEO";

const TOKEN_KEY = "gsp_admin_token";

const authHeaders = () => {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// ---------------- Login form ----------------
const LoginView = ({ onLoggedIn }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Enter email and password.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      toast.success("Welcome back.");
      onLoggedIn(data.user);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login" className="min-h-screen mesh-bg grain text-white flex items-center justify-center px-6 py-24">
      <div className="w-full max-w-md">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-white/40 mb-4 flex items-center gap-2">
          <ShieldCheck size={12} /> Admin · Growth Systems Portfolio
        </div>
        <h1 className="font-display text-4xl leading-tight tracking-tight">Sign in to your dashboard.</h1>
        <p className="mt-3 text-white/60 text-sm">Only Shivani has access. Contact form submissions land here.</p>

        <form onSubmit={onSubmit} className="mt-10 glass-dark rounded-2xl p-6 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-[0.15em] text-white/40 mb-2 block">Email</label>
            <input
              data-testid="admin-email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-white/[0.03] border border-white/10 focus:border-[#3B82F6] outline-none rounded-lg px-4 py-3 text-white placeholder-white/30"
              placeholder="admin@example.com"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.15em] text-white/40 mb-2 block">Password</label>
            <input
              data-testid="admin-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-white/[0.03] border border-white/10 focus:border-[#3B82F6] outline-none rounded-lg px-4 py-3 text-white placeholder-white/30"
              placeholder="••••••••"
            />
          </div>
          <button
            data-testid="admin-login-submit"
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-white text-neutral-900 px-5 py-3 text-sm font-medium hover:bg-white/90 transition disabled:opacity-60"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
};

// ---------------- Chat Sessions tab ----------------
const ChatSessions = ({ onLogout }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [transcript, setTranscript] = useState([]);
  const [txLoading, setTxLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/chat/sessions`, { headers: authHeaders() });
      setSessions(data);
      if (data.length && !selected) setSelected(data[0]);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        toast.error("Session expired.");
        onLogout();
      } else {
        toast.error("Failed to load chat sessions.");
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onLogout]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!selected) return;
    setTxLoading(true);
    axios
      .get(`${API}/admin/chat/sessions/${encodeURIComponent(selected.session_id)}`, { headers: authHeaders() })
      .then(({ data }) => setTranscript(data))
      .catch(() => toast.error("Failed to load transcript."))
      .finally(() => setTxLoading(false));
  }, [selected]);

  const fmt = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
      <div className="lg:col-span-5 glass-dark rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/5">
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/40">Sessions ({sessions.length})</div>
          <button data-testid="chat-sessions-refresh" onClick={load} className="text-xs text-white/60 hover:text-white inline-flex items-center gap-1"><RefreshCcw size={12} /> Refresh</button>
        </div>
        {loading ? (
          <div className="p-10 text-center text-white/50 text-sm inline-flex items-center gap-2 justify-center w-full">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        ) : sessions.length === 0 ? (
          <div className="p-10 text-center text-white/50 text-sm">No chat sessions yet.</div>
        ) : (
          <ul className="divide-y divide-white/5 max-h-[70vh] overflow-y-auto">
            {sessions.map((s) => {
              const active = selected?.session_id === s.session_id;
              return (
                <li key={s.session_id}>
                  <button
                    data-testid={`chat-session-row-${s.session_id}`}
                    onClick={() => setSelected(s)}
                    className={`w-full text-left px-5 py-4 transition ${active ? "bg-[#3B82F6]/10" : "hover:bg-white/[0.02]"}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-mono text-[10px] text-white/40 truncate">{s.session_id}</div>
                        <div className="mt-1 text-xs text-white/70 line-clamp-2">
                          <span className="text-white/40">{s.last_role === "user" ? "Visitor: " : "AI: "}</span>
                          {s.last_content}
                        </div>
                      </div>
                      <div className="text-[10px] font-mono text-white/40 whitespace-nowrap text-right">
                        <div>{s.message_count} msgs</div>
                        <div>{fmt(s.last_at)}</div>
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="lg:col-span-7">
        {selected ? (
          <div data-testid="chat-transcript" className="glass-dark rounded-2xl p-6">
            <div className="flex items-center justify-between border-b border-white/5 pb-3 mb-4">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/40">Transcript</div>
                <div className="mt-1 font-mono text-xs text-white/70 break-all">{selected.session_id}</div>
              </div>
              <div className="text-[10px] font-mono text-white/40 text-right">
                {selected.message_count} messages<br />
                {fmt(selected.first_at)} → {fmt(selected.last_at)}
              </div>
            </div>
            {txLoading ? (
              <div className="text-center py-10 text-white/50"><Loader2 className="animate-spin inline" size={14} /></div>
            ) : (
              <div className="space-y-3 max-h-[60vh] overflow-y-auto">
                {transcript.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-[#3B82F6] text-white" : "bg-white/[0.04] border border-white/5 text-white/90"}`}>
                      {m.content}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="glass-dark rounded-2xl p-10 text-center text-white/40 text-sm">Select a session to view its transcript.</div>
        )}
      </div>
    </div>
  );
};

// ---------------- Dashboard ----------------
const Dashboard = ({ user, onLogout }) => {
  const [tab, setTab] = useState("messages"); // messages | chat
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/messages`, { headers: authHeaders() });
      setMessages(data);
      if (data.length && !selected) setSelected(data[0]);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        toast.error("Session expired. Please sign in again.");
        onLogout();
      } else {
        toast.error("Failed to load messages.");
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onLogout]);

  useEffect(() => { load(); }, [load]);

  const filtered = messages.filter((m) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (
      m.name?.toLowerCase().includes(s) ||
      m.email?.toLowerCase().includes(s) ||
      (m.company || "").toLowerCase().includes(s) ||
      m.message?.toLowerCase().includes(s)
    );
  });

  const fmt = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#0F1115] text-white">
      {/* Top bar */}
      <header className="border-b border-white/5 bg-[#0F1115]/80 backdrop-blur">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 rounded-full bg-[#10B981] pulse-dot" />
            <div className="font-display text-lg">Growth Systems · Admin</div>
            <div className="hidden md:block text-xs font-mono text-white/40">/ {user?.email}</div>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="admin-refresh"
              onClick={load}
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs hover:border-white/40"
            >
              <RefreshCcw size={12} /> Refresh
            </button>
            <button
              data-testid="admin-logout"
              onClick={onLogout}
              className="inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/10 px-4 py-2 text-xs hover:bg-white/10"
            >
              <LogOut size={12} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-8">
        {/* Tabs */}
        <div className="flex items-center gap-2 mb-6">
          <button
            data-testid="admin-tab-messages"
            onClick={() => setTab("messages")}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium transition ${tab === "messages" ? "bg-white text-neutral-900" : "border border-white/15 text-white/70 hover:border-white/40"}`}
          >
            <Mail size={12} /> Contact Leads
          </button>
          <button
            data-testid="admin-tab-chat"
            onClick={() => setTab("chat")}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium transition ${tab === "chat" ? "bg-white text-neutral-900" : "border border-white/15 text-white/70 hover:border-white/40"}`}
          >
            <MessageSquare size={12} /> AI Chat Sessions
          </button>
        </div>

        {tab === "chat" ? (
          <ChatSessions onLogout={onLogout} />
        ) : (
        <>
        {/* KPI row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {[
            { l: "Total Leads", v: messages.length },
            { l: "This Week", v: messages.filter((m) => (Date.now() - new Date(m.created_at).getTime()) < 7 * 864e5).length },
            { l: "With Company", v: messages.filter((m) => m.company).length },
            { l: "Latest", v: messages[0] ? fmt(messages[0].created_at).split(",")[0] : "—" },
          ].map((k) => (
            <div key={k.l} data-testid={`admin-kpi-${k.l.replace(/\s/g, "-").toLowerCase()}`} className="glass-dark rounded-2xl p-5">
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">{k.l}</div>
              <div className="mt-2 font-display text-3xl">{k.v}</div>
            </div>
          ))}
        </div>

        {/* Search */}
        <div className="glass-dark rounded-2xl px-4 py-3 mb-4 flex items-center gap-3">
          <Search size={14} className="text-white/40" />
          <input
            data-testid="admin-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, email, company, or message…"
            className="flex-1 bg-transparent outline-none text-sm placeholder-white/30"
          />
          <span className="text-xs font-mono text-white/40">{filtered.length} / {messages.length}</span>
        </div>

        {/* Grid: list + detail */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          <div className="lg:col-span-5 glass-dark rounded-2xl overflow-hidden">
            {loading ? (
              <div className="p-10 text-center text-white/50 text-sm inline-flex items-center gap-2 justify-center w-full">
                <Loader2 size={14} className="animate-spin" /> Loading messages…
              </div>
            ) : filtered.length === 0 ? (
              <div data-testid="admin-empty" className="p-10 text-center text-white/50 text-sm">No messages yet.</div>
            ) : (
              <ul className="divide-y divide-white/5 max-h-[70vh] overflow-y-auto">
                {filtered.map((m) => {
                  const active = selected?.id === m.id;
                  return (
                    <li key={m.id}>
                      <button
                        data-testid={`admin-row-${m.id}`}
                        onClick={() => setSelected(m)}
                        className={`w-full text-left px-5 py-4 transition ${active ? "bg-[#3B82F6]/10" : "hover:bg-white/[0.02]"}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="font-display text-base">{m.name}</div>
                            <div className="text-xs text-white/50 mt-0.5">{m.email}</div>
                          </div>
                          <div className="text-[10px] font-mono text-white/40 whitespace-nowrap">{fmt(m.created_at)}</div>
                        </div>
                        <div className="text-xs text-white/60 mt-2 line-clamp-2">{m.message}</div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="lg:col-span-7">
            {selected ? (
              <div data-testid="admin-detail" className="glass-dark rounded-2xl p-6 md:p-8">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">Lead</div>
                    <div className="font-display text-3xl mt-1">{selected.name}</div>
                  </div>
                  <div className="text-xs font-mono text-white/50">{fmt(selected.created_at)}</div>
                </div>

                <div className="mt-6 grid sm:grid-cols-2 gap-3">
                  <a
                    data-testid="admin-reply-email"
                    href={`mailto:${selected.email}?subject=Re: Your growth inquiry`}
                    className="rounded-xl bg-white/[0.03] border border-white/5 p-4 hover:border-[#3B82F6] transition flex items-center gap-3"
                  >
                    <Mail size={16} className="text-[#3B82F6]" />
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/40">Email</div>
                      <div className="text-sm">{selected.email}</div>
                    </div>
                  </a>
                  <div className="rounded-xl bg-white/[0.03] border border-white/5 p-4 flex items-center gap-3">
                    <Building2 size={16} className="text-[#10B981]" />
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/40">Company</div>
                      <div className="text-sm">{selected.company || "—"}</div>
                    </div>
                  </div>
                </div>

                <div className="mt-6">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-3">Message</div>
                  <div className="rounded-xl bg-white/[0.02] border border-white/5 p-5 whitespace-pre-wrap text-sm leading-relaxed text-white/85">
                    {selected.message}
                  </div>
                </div>
              </div>
            ) : (
              <div className="glass-dark rounded-2xl p-10 text-center text-white/40 text-sm">Select a lead to view details.</div>
            )}
          </div>
        </div>
        </>
        )}
      </div>
    </div>
  );
};

// ---------------- Root ----------------
export default function Admin() {
  useSEO({
    title: "Admin · Growth Systems Portfolio",
    description: "Private lead dashboard for the Growth Systems Portfolio.",
    path: "/admin",
  });
  const [user, setUser] = useState(null); // null = checking, false = logged out, object = logged in
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setChecking(false); setUser(false); return; }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
        setUser(data);
      } catch {
        localStorage.removeItem(TOKEN_KEY);
        setUser(false);
      } finally {
        setChecking(false);
      }
    })();
  }, []);

  const onLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(false);
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-[#0F1115] text-white flex items-center justify-center">
        <Loader2 className="animate-spin" />
      </div>
    );
  }
  if (!user) return <LoginView onLoggedIn={setUser} />;
  return <Dashboard user={user} onLogout={onLogout} />;
}
